import {
  Activity,
  Building,
  Eye,
  FileText,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Server,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { Organization, OrganizationCreate, OrganizationUpdate } from '../../types';
import organizationService from '../../services/organizationService';
import configurationService from '../../services/configurationService';
import forgeService from '../../services/forgeService';
import tokenEconomyService, {
  type OrganizationQuotaRow,
} from '../../services/tokenEconomyService';
import userService from '@/services/userService';
import { useIsSuperAdmin } from '../../stores/authStore';
import CreateOrganizationModal from '@/components/Organizations/CreateOrganizationModal';
import EditOrganizationModal from '@/components/Organizations/EditOrganizationModal';
import ImportOrganizationWizard from '@/components/Organizations/ImportWizard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/Common/Card';
import { Button } from '@/components/Common/button';
import { Input } from '@/components/Common/input';
import { ConfirmationModal } from '@/components/Common/ConfirmationModal';
import { PageLayout } from '@/components/Layout/PageLayout';
import { useTranslation } from 'react-i18next';
import { notify } from '@/lib/notify';

function pickQuotaColor(pct: number): string {
  if (pct >= 100) return 'hsl(0, 80%, 55%)';
  if (pct >= 90) return 'hsl(0, 75%, 55%)';
  if (pct >= 60) return 'hsl(35, 90%, 50%)';
  return 'hsl(140, 60%, 40%)';
}

type StatusFilter = 'all' | 'active' | 'inactive';
type ForgeFilter = 'all' | 'linked' | 'unlinked';
type PageTab = 'all' | 'demo';

const OrganizationsPage: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [organizationToDelete, setOrganizationToDelete] = useState<Organization | null>(null);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showDeleteBlockedModal, setShowDeleteBlockedModal] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [quotaByOrg, setQuotaByOrg] = useState<Record<string, OrganizationQuotaRow>>({});
  const [forgeClientNames, setForgeClientNames] = useState<Record<string, string>>({});

  // Demo orgs tab state
  const [activeTab, setActiveTab] = useState<PageTab>('all');
  const [demoOrganizations, setDemoOrganizations] = useState<Organization[]>([]);
  const [loadingDemo, setLoadingDemo] = useState(false);
  const [demoError, setDemoError] = useState<string | null>(null);
  const [selectedDemoOrgIds, setSelectedDemoOrgIds] = useState<Set<string>>(new Set());
  const [showDeleteAllDemoOrgsDialog, setShowDeleteAllDemoOrgsDialog] = useState(false);
  const [showDeleteSelectedDemoOrgsDialog, setShowDeleteSelectedDemoOrgsDialog] = useState(false);
  const [deletingDemoOrgs, setDeletingDemoOrgs] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);

  // Filters (all tab)
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [forgeFilter, setForgeFilter] = useState<ForgeFilter>('all');
  const [sectorFilter, setSectorFilter] = useState<string>('all');

  const isSuperAdmin = useIsSuperAdmin();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (isSuperAdmin) {
      loadOrganizations();
      loadQuotas();
      loadForgeClients();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    if (activeTab === 'demo') loadDemoOrganizations();
  }, [isSuperAdmin, activeTab]);

  const loadQuotas = async () => {
    try {
      const data = await tokenEconomyService.listOrganizationQuotas();
      const map: Record<string, OrganizationQuotaRow> = {};
      for (const row of data.items) map[row.organization_id] = row;
      setQuotaByOrg(map);
    } catch (err) {
      console.error('Error loading GenieArtifact quotas:', err);
    }
  };

  const loadForgeClients = async () => {
    try {
      const clients = await forgeService.listClients();
      const map: Record<string, string> = {};
      for (const c of clients) map[c.id ?? c.client_id] = c.name;
      setForgeClientNames(map);
    } catch (err) {
      console.error('Error loading Forge clients:', err);
    }
  };

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const data = await organizationService.getOrganizations({ includeInactive: true });
      setOrganizations(data);
    } catch (err) {
      setError(t('organizations.page.loadError', 'Erreur lors du chargement des organisations'));
      console.error('Error loading organizations:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDemoOrganizations = useCallback(async () => {
    setLoadingDemo(true);
    setDemoError(null);
    try {
      const data = await organizationService.getDemoOrganizations();
      setDemoOrganizations(data);
      setSelectedDemoOrgIds(new Set());
    } catch (err) {
      setDemoError(t('organizations.page.demoTab.loadError', 'Failed to load demo organizations'));
      console.error('Error loading demo organizations:', err);
    } finally {
      setLoadingDemo(false);
    }
  }, [t]);

  const handleCreateOrganization = async (organizationData: OrganizationCreate) => {
    try {
      const newOrganization = await organizationService.createOrganization(organizationData);
      setOrganizations(prev => [...prev, newOrganization]);
      setShowCreateModal(false);
      try {
        await configurationService.loadTemplate(newOrganization.id, organizationData.sector_template_id);
      } catch (templateError) {
        setError(
          t(
            'organizations.page.templateLoadError',
            "L'organisation a été créée mais le template du secteur n'a pas pu être chargé automatiquement."
          )
        );
        console.error('Error loading sector template after organization creation:', templateError);
      }
    } catch (err) {
      setError(t('organizations.page.createError', "Erreur lors de la création de l'organisation"));
      console.error('Error creating organization:', err);
    }
  };

  const handleUpdateOrganization = async (id: string, organizationData: OrganizationUpdate) => {
    try {
      const previousOrganization = organizations.find((org) => org.id === id) ?? null;
      const sectorChanged =
        organizationData.sector_template_id !== undefined &&
        organizationData.sector_template_id !== previousOrganization?.sector_template_id;

      const updatedOrganization = await organizationService.updateOrganization(id, organizationData);
      setOrganizations(prev =>
        prev.map(org => org.id === id ? updatedOrganization : org)
      );

      if (sectorChanged && organizationData.sector_template_id) {
        try {
          await configurationService.loadTemplate(id, organizationData.sector_template_id);
        } catch (templateError) {
          setError(
            t(
              'organizations.page.sectorChangeTemplateLoadError',
              "Le secteur a bien été modifié, mais la réinitialisation automatique de la configuration a échoué."
            )
          );
          console.error('Error reloading sector template after organization update:', templateError);
        }
      }

      setShowEditModal(false);
      setSelectedOrganization(null);
    } catch (err) {
      setError(t('organizations.page.updateError', "Erreur lors de la mise à jour de l'organisation"));
      console.error('Error updating organization:', err);
    }
  };

  const isDeleteBlockedByExistingUsers = (detail: unknown) =>
    typeof detail === 'string' && detail.includes('Cannot delete organization with existing users');

  const handleDeleteClick = async (organization: Organization) => {
    try {
      const users = await organizationService.getOrganizationUsers(organization.id);
      if (users.length > 0) {
        setShowDeleteBlockedModal(true);
        return;
      }
      setOrganizationToDelete(organization);
      setDeleteConfirmInput('');
      setShowDeleteModal(true);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      if (isDeleteBlockedByExistingUsers(detail)) {
        setShowDeleteBlockedModal(true);
      } else {
        setError(t('organizations.page.deleteError', "Erreur lors de la suppression de l'organisation"));
      }
      console.error('Error checking organization delete constraints:', err);
    }
  };

  const handleConfirmDelete = async () => {
    if (!organizationToDelete) return;
    if (deleteConfirmInput !== organizationToDelete.name) return;

    setDeleting(true);
    try {
      await organizationService.deleteOrganization(organizationToDelete.id);
      setOrganizations(prev => prev.filter(org => org.id !== organizationToDelete.id));
      setShowDeleteModal(false);
      setOrganizationToDelete(null);
      setDeleteConfirmInput('');
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      if (isDeleteBlockedByExistingUsers(detail)) {
        setShowDeleteModal(false);
        setOrganizationToDelete(null);
        setShowDeleteBlockedModal(true);
      } else {
        setError(t('organizations.page.deleteError', "Erreur lors de la suppression de l'organisation"));
      }
      console.error('Error deleting organization:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setOrganizationToDelete(null);
    setDeleteConfirmInput('');
  };

  const handleEditClick = async (organization: Organization) => {
    try {
      const organizationWithDetails = await organizationService.getOrganizationById(organization.id);
      setSelectedOrganization(organizationWithDetails);
    } catch (err) {
      setSelectedOrganization(organization);
      setError(
        t(
          'organizations.page.editLoadError',
          "Impossible de charger tous les détails de l'organisation. Vérifiez les champs avant de sauvegarder."
        )
      );
      console.error('Error loading organization details for edit:', err);
    } finally {
      setShowEditModal(true);
    }
  };

  const handleDetailsClick = (organization: Organization) => {
    navigate(`/organizations/${organization.id}`);
  };

  const sectors = useMemo(() => {
    const set = new Set<string>();
    organizations.forEach((o) => {
      if (o.sector_template_id) set.add(o.sector_template_id);
    });
    return Array.from(set).sort();
  }, [organizations]);

  const filteredOrganizations = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return organizations.filter((org) => {
      if (q) {
        const haystack = `${org.name} ${org.description ?? ''} ${org.domain ?? ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (statusFilter === 'active' && !org.is_active) return false;
      if (statusFilter === 'inactive' && org.is_active) return false;
      if (forgeFilter === 'linked' && !org.forge_client_id) return false;
      if (forgeFilter === 'unlinked' && org.forge_client_id) return false;
      if (sectorFilter !== 'all' && (org.sector_template_id ?? '') !== sectorFilter) return false;
      return true;
    });
  }, [organizations, searchTerm, statusFilter, forgeFilter, sectorFilter]);

  const hasActiveFilter =
    !!searchTerm ||
    statusFilter !== 'all' ||
    forgeFilter !== 'all' ||
    sectorFilter !== 'all';

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setForgeFilter('all');
    setSectorFilter('all');
  };

  // Demo orgs handlers
  const toggleDemoOrgSelection = (id: string) => {
    setSelectedDemoOrgIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllDemoOrgSelection = () => {
    if (selectedDemoOrgIds.size === demoOrganizations.length) {
      setSelectedDemoOrgIds(new Set());
    } else {
      setSelectedDemoOrgIds(new Set(demoOrganizations.map((o) => o.id)));
    }
  };

  const handleDeleteDemoOrgs = async (ids: string[]) => {
    setDeletingDemoOrgs(true);
    let successCount = 0;
    let errorCount = 0;
    for (const id of ids) {
      try {
        // Demo orgs have demo users — delete users first, then the now-empty org
        const orgUsers = await organizationService.getOrganizationUsers(id);
        for (const user of orgUsers) {
          await userService.deleteUser(user.id);
        }
        await organizationService.deleteOrganization(id);
        successCount++;
      } catch {
        errorCount++;
      }
    }
    setDeletingDemoOrgs(false);
    setShowDeleteAllDemoOrgsDialog(false);
    setShowDeleteSelectedDemoOrgsDialog(false);
    if (successCount > 0) {
      notify.success(
        t('organizations.page.demoTab.deletedCount', { count: successCount }),
        t('organizations.page.demoTab.deleteSuccess', 'Demo organizations deleted'),
      );
    }
    if (errorCount > 0) {
      notify.error(
        t('organizations.page.demoTab.deleteError', 'Some deletions failed') +
          ' ' +
          t('organizations.page.demoTab.errorCount', { count: errorCount }),
      );
    }
    loadDemoOrganizations();
  };

  const handleCleanupExpired = async () => {
    setCleaningUp(true);
    try {
      const result = await organizationService.triggerDemoCleanup();
      notify.success(
        t('organizations.page.demoTab.cleanupResult', '{{count}} expired session(s) deleted.', { count: result.deleted }),
        t('organizations.page.demoTab.cleanupSuccess', 'Cleanup complete'),
      );
      loadDemoOrganizations();
    } catch {
      notify.error(t('organizations.page.demoTab.cleanupError', 'Cleanup failed'));
    } finally {
      setCleaningUp(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="w-full flex items-center justify-center py-16">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <Building className="h-5 w-5 text-muted-foreground" />
              {t('organizations.page.unauthorizedTitle', 'Accès non autorisé')}
            </CardTitle>
            <CardDescription>
              {t(
                'organizations.page.unauthorizedDescription',
                'Seuls les super-administrateurs peuvent accéder à cette page.',
              )}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (loading && activeTab === 'all') {
    return (
      <div className="w-full flex items-center justify-center py-16">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>{t('organizations.page.loading', 'Chargement des organisations...')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  const tabs: { id: PageTab; labelKey: string; label: string }[] = [
    { id: 'all', labelKey: 'organizations.page.tabAll', label: 'All' },
    { id: 'demo', labelKey: 'organizations.page.demoOrgsTab', label: 'Demo orgs' },
  ];

  return (
    <PageLayout
      icon={<Building className="h-6 w-6" />}
      title={t('organizations.page.title', 'Gestion des Organisations')}
      subtitle={t('organizations.page.subtitle', 'Gérez les organisations et leurs utilisateurs')}
    >
      <div className="space-y-4">
        {/* Header actions */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-2">
            <Link to="/templates">
              <Button variant="outline">
                <FileText className="h-4 w-4 mr-2" /> {t('organizations.page.templatesButton', 'Templates')}
              </Button>
            </Link>
            <Button variant="outline" onClick={() => setShowImportWizard(true)}>
              <Upload className="h-4 w-4 mr-2" />{' '}
              {t('organizations.page.importOrganization', 'Importer')}
            </Button>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" /> {t('organizations.page.newOrganization', 'Nouvelle Organisation')}
            </Button>
          </div>
        </div>

        {error && (
          <Card className="border-destructive/40">
            <CardHeader>
              <CardTitle className="text-destructive text-base">{t('global.error')}</CardTitle>
              <CardDescription className="text-destructive/90">{error}</CardDescription>
            </CardHeader>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(tab.labelKey, tab.label)}
            </button>
          ))}
        </div>

        {/* All organizations tab */}
        {activeTab === 'all' && (
          <>
            <Card>
              <CardContent className="p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative w-full sm:w-56">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={t('organizations.page.filters.searchPlaceholder', 'Rechercher...')}
                      className="h-9 pl-8 text-sm"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                    className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                  >
                    <option value="all">{t('organizations.page.filters.statusAll', 'Tous les statuts')}</option>
                    <option value="active">{t('organizationGeneralInfo.active', 'Actif')}</option>
                    <option value="inactive">{t('organizationGeneralInfo.inactive', 'Inactif')}</option>
                  </select>
                  <select
                    value={forgeFilter}
                    onChange={(e) => setForgeFilter(e.target.value as ForgeFilter)}
                    className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                  >
                    <option value="all">{t('organizations.page.filters.forgeAll', 'Forge: tous')}</option>
                    <option value="linked">{t('organizations.page.filters.forgeLinked', 'Forge: lié')}</option>
                    <option value="unlinked">{t('organizations.page.filters.forgeUnlinked', 'Forge: non lié')}</option>
                  </select>
                  {sectors.length > 0 && (
                    <select
                      value={sectorFilter}
                      onChange={(e) => setSectorFilter(e.target.value)}
                      className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                    >
                      <option value="all">{t('organizations.page.filters.sectorAll', 'Tous les secteurs')}</option>
                      {sectors.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  )}
                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {t('organizations.page.filters.resultCount', '{{filtered}} sur {{total}}', {
                        filtered: filteredOrganizations.length,
                        total: organizations.length,
                      })}
                    </span>
                    {hasActiveFilter && (
                      <Button variant="ghost" size="sm" onClick={resetFilters} className="h-8">
                        <X className="h-4 w-4 mr-1" />
                        {t('organizations.page.filters.reset', 'Réinitialiser')}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                {filteredOrganizations.length === 0 ? (
                  <div className="py-12 text-center">
                    <Building className="mx-auto h-10 w-10 text-muted-foreground" />
                    <div className="mt-3 font-medium">
                      {organizations.length === 0
                        ? t('organizations.page.emptyTitle', 'Aucune organisation')
                        : t('organizations.page.filters.noMatch', 'Aucune organisation ne correspond aux filtres.')}
                    </div>
                    {organizations.length === 0 && (
                      <CardDescription className="mt-1">
                        {t(
                          'organizations.page.emptyDescription',
                          'Commencez par créer votre première organisation.',
                        )}
                      </CardDescription>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="px-4 py-3 font-medium">
                            {t('organizations.page.table.organization', 'Organisation')}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t('organizations.page.table.domain', 'Domaine')}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t('organizations.page.table.status', 'Statut')}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t('organizations.page.table.forgeClient', 'Client Forge')}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t('organizations.page.table.quota', 'Quota IA')}
                          </th>
                          <th className="px-4 py-3 text-right font-medium">
                            {t('organizations.page.table.actions', 'Actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrganizations.map((organization) => {
                          const quota = quotaByOrg[organization.id];
                          const pct = quota ? Math.min(quota.percentage, 100) : 0;
                          const color = quota ? pickQuotaColor(quota.percentage) : 'hsl(140, 60%, 40%)';
                          return (
                            <tr
                              key={organization.id}
                              className="border-b border-border last:border-0 hover:bg-muted/30"
                            >
                              <td className="px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() => handleDetailsClick(organization)}
                                  className="flex items-start gap-2 text-left"
                                >
                                  <Building className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                                  <div className="min-w-0">
                                    <div className="font-medium hover:underline">{organization.name}</div>
                                    {organization.description && (
                                      <div className="line-clamp-1 text-xs text-muted-foreground">
                                        {organization.description}
                                      </div>
                                    )}
                                  </div>
                                </button>
                              </td>
                              <td className="px-4 py-3 text-muted-foreground">
                                {organization.domain || '—'}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                    organization.is_active
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  }`}
                                >
                                  {organization.is_active
                                    ? t('organizationGeneralInfo.active', 'Actif')
                                    : t('organizationGeneralInfo.inactive', 'Inactif')}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-muted-foreground">
                                {organization.forge_client_id ? (
                                  <span className="inline-flex items-center gap-1">
                                    <Server className="h-3 w-3" />
                                    <span className="text-foreground">
                                      {forgeClientNames[organization.forge_client_id] ??
                                        organization.forge_client_id}
                                    </span>
                                  </span>
                                ) : (
                                  <span className="italic">
                                    {t('organizations.page.forgeClientNone', 'non configuré')}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {quota ? (
                                  <div className="flex min-w-[140px] flex-col gap-1">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                                        <Activity className="h-3 w-3" />
                                        {Math.round(quota.percentage)}%
                                      </span>
                                      <span className="text-muted-foreground">
                                        {Math.round(quota.used).toLocaleString()} /{' '}
                                        {Math.round(quota.effective_quota).toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                                      <div
                                        className="h-full"
                                        style={{ width: `${pct}%`, background: color }}
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="inline-flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={t('organizations.page.table.viewDetails', 'Voir')}
                                    title={t('organizations.page.table.viewDetails', 'Voir')}
                                    onClick={() => handleDetailsClick(organization)}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={t('global.edit', 'Modifier')}
                                    title={t('global.edit', 'Modifier')}
                                    onClick={() => handleEditClick(organization)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={t('global.delete', 'Supprimer')}
                                    title={t('global.delete', 'Supprimer')}
                                    onClick={() => handleDeleteClick(organization)}
                                    className="text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Demo organizations tab */}
        {activeTab === 'demo' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle>{t('organizations.page.demoOrgsTab', 'Demo orgs')}</CardTitle>
                  <CardDescription className="mt-1">
                    {t('organizations.page.demoTab.description', 'Demo organizations (is_demo=true).')}
                    {demoOrganizations.length > 0 && (
                      <span className="ml-2 text-muted-foreground">
                        — {t('organizations.page.demoTab.totalCount', { count: demoOrganizations.length })}
                      </span>
                    )}
                  </CardDescription>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCleanupExpired}
                    disabled={cleaningUp}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${cleaningUp ? 'animate-spin' : ''}`} />
                    {cleaningUp
                      ? t('organizations.page.demoTab.cleaning', 'Cleaning…')
                      : t('organizations.page.demoTab.cleanupExpired', 'Cleanup expired (>24h)')}
                  </Button>
                  {selectedDemoOrgIds.size > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowDeleteSelectedDemoOrgsDialog(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t('organizations.page.demoTab.deleteSelected', 'Delete selected')} ({selectedDemoOrgIds.size})
                    </Button>
                  )}
                  {demoOrganizations.length > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowDeleteAllDemoOrgsDialog(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t('organizations.page.demoTab.deleteAll', 'Delete all demo orgs')}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingDemo ? (
                <div className="flex justify-center py-8">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              ) : demoError ? (
                <p className="text-destructive text-sm">{demoError}</p>
              ) : demoOrganizations.length === 0 ? (
                <p className="text-muted-foreground text-center py-6">
                  {t('organizations.page.demoTab.noOrgs', 'No demo organizations.')}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground text-xs uppercase">
                        <th className="py-2 px-3 text-left w-8">
                          <input
                            type="checkbox"
                            checked={selectedDemoOrgIds.size === demoOrganizations.length && demoOrganizations.length > 0}
                            onChange={toggleAllDemoOrgSelection}
                            aria-label={t('global.selectAll', 'Select all')}
                            className="rounded"
                          />
                        </th>
                        <th className="text-left py-2 px-3">{t('organizations.page.table.organization', 'Organisation')}</th>
                        <th className="text-left py-2 px-3">{t('admin.users.table.created', 'Created')}</th>
                        <th className="text-left py-2 px-3">{t('organizations.page.demoTab.colUsers', 'Users')}</th>
                        <th className="text-left py-2 px-3">{t('organizations.page.table.status', 'Status')}</th>
                        <th className="text-right py-2 px-3">{t('organizations.page.table.actions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {demoOrganizations.map((org) => (
                        <tr key={org.id} className="border-b hover:bg-muted/40">
                          <td className="py-2 px-3">
                            <input
                              type="checkbox"
                              checked={selectedDemoOrgIds.has(org.id)}
                              onChange={() => toggleDemoOrgSelection(org.id)}
                              aria-label={t('organizations.page.demoTab.selectOrg', { name: org.name })}
                              className="rounded"
                            />
                          </td>
                          <td className="py-2 px-3 font-medium">{org.name}</td>
                          <td className="py-2 px-3 text-muted-foreground">
                            {org.created_at
                              ? new Date(org.created_at).toLocaleDateString()
                              : '—'}
                          </td>
                          <td className="py-2 px-3 text-muted-foreground">—</td>
                          <td className="py-2 px-3">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                org.is_active
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}
                            >
                              {org.is_active
                                ? t('organizationGeneralInfo.active', 'Actif')
                                : t('organizationGeneralInfo.inactive', 'Inactif')}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDeleteDemoOrgs([org.id])}
                              disabled={deletingDemoOrgs}
                              aria-label={t('global.delete', 'Supprimer')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateOrganizationModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateOrganization}
        />
      )}

      {showEditModal && selectedOrganization && (
        <EditOrganizationModal
          organization={selectedOrganization}
          onClose={() => {
            setShowEditModal(false);
            setSelectedOrganization(null);
          }}
          onSubmit={(data: OrganizationUpdate) => handleUpdateOrganization(selectedOrganization.id, data)}
        />
      )}

      <ImportOrganizationWizard
        open={showImportWizard}
        onClose={() => setShowImportWizard(false)}
        onImported={async () => {
          try {
            const data = await organizationService.getOrganizations({ includeInactive: true });
            setOrganizations(data);
          } catch (err) {
            console.error('Error refreshing organizations after import:', err);
          }
        }}
      />

      <ConfirmationModal
        open={showDeleteModal}
        title={t('organizations.page.deleteTitle', "Supprimer l'organisation")}
        message={t('organizations.page.deleteMessage', {
          name: organizationToDelete?.name ?? '',
        })}
        confirmText={t('global.delete')}
        cancelText={t('global.cancel')}
        variant="danger"
        requireTextMatch={
          organizationToDelete
            ? {
                text: organizationToDelete.name,
                label: t(
                  'organizations.page.deleteConfirmLabel',
                  "Tapez le nom de l'organisation pour confirmer",
                ),
              }
            : undefined
        }
        inputValue={deleteConfirmInput}
        onInputChange={setDeleteConfirmInput}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={deleting}
      />

      <ConfirmationModal
        open={showDeleteBlockedModal}
        title={t('organizations.page.deleteBlockedTitle', "Can't delete this orga !")}
        message={t(
          'organizations.page.deleteBlockedMessage',
          'Cannot delete organization with existing users. Please reassign or delete users first.',
        )}
        cancelText={t('global.close', 'Close')}
        variant="info"
        hideConfirmButton
        onConfirm={() => setShowDeleteBlockedModal(false)}
        onCancel={() => setShowDeleteBlockedModal(false)}
      />

      {/* Delete all demo orgs confirmation */}
      <ConfirmationModal
        open={showDeleteAllDemoOrgsDialog}
        title={t('organizations.page.demoTab.confirmDeleteAllTitle', 'Delete all demo organizations')}
        message={t(
          'organizations.page.demoTab.confirmDeleteAllMessage',
          'You are about to permanently delete {{count}} demo organization(s). This action cannot be undone.',
          { count: demoOrganizations.length }
        )}
        confirmText={t('global.delete', 'Delete')}
        cancelText={t('global.cancel', 'Cancel')}
        variant="danger"
        onConfirm={() => handleDeleteDemoOrgs(demoOrganizations.map((o) => o.id))}
        onCancel={() => setShowDeleteAllDemoOrgsDialog(false)}
        loading={deletingDemoOrgs}
      >
        <ul className="max-h-40 overflow-y-auto rounded-md border border-border bg-muted/40 divide-y divide-border text-xs">
          {demoOrganizations.map((o) => (
            <li key={o.id} className="px-3 py-1.5 text-foreground truncate">{o.name}</li>
          ))}
        </ul>
      </ConfirmationModal>

      {/* Delete selected demo orgs confirmation */}
      <ConfirmationModal
        open={showDeleteSelectedDemoOrgsDialog}
        title={t('organizations.page.demoTab.confirmDeleteSelectedTitle', 'Delete selected demo organizations')}
        message={t(
          'organizations.page.demoTab.confirmDeleteSelectedMessage',
          'You are about to permanently delete {{count}} organization(s). This action cannot be undone.',
          { count: selectedDemoOrgIds.size }
        )}
        confirmText={t('global.delete', 'Delete')}
        cancelText={t('global.cancel', 'Cancel')}
        variant="danger"
        onConfirm={() => handleDeleteDemoOrgs(Array.from(selectedDemoOrgIds))}
        onCancel={() => setShowDeleteSelectedDemoOrgsDialog(false)}
        loading={deletingDemoOrgs}
      >
        <ul className="max-h-40 overflow-y-auto rounded-md border border-border bg-muted/40 divide-y divide-border text-xs">
          {demoOrganizations
            .filter((o) => selectedDemoOrgIds.has(o.id))
            .map((o) => (
              <li key={o.id} className="px-3 py-1.5 text-foreground truncate">{o.name}</li>
            ))}
        </ul>
      </ConfirmationModal>
    </PageLayout>
  );
};

export default OrganizationsPage;
